import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Clock } from "@phosphor-icons/react";
import { useT } from "../i18n";
import api from "../lib/api";

const BlogArticle = () => {
  const { slug } = useParams();
  const { t } = useT();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    api.get(`/blog/${slug}`).then(({ data }) => {
      setPost(data);
      document.title = `${data.title} | YADIMMO`;
      let meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", data.excerpt);
    }).catch(() => navigate("/blog")).finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading || !post) {
    return <div className="pt-40 pb-40 text-center text-muted-foreground min-h-screen">{t("blog.generating")}</div>;
  }

  return (
    <div className="relative z-10 pt-[72px] min-h-screen bg-background">
      <div className="relative h-[380px]">
        <img src={post.cover} alt={post.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute bottom-0 inset-x-0 max-w-3xl mx-auto px-5 sm:px-8 pb-10">
          <div className="text-xs text-gold font-semibold uppercase tracking-wider mb-3">{post.category}</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white font-light leading-tight">{post.title}</h1>
          <div className="flex items-center gap-2 text-white/70 text-sm mt-4"><Clock size={16} /> {post.read_time} {t("blog.min")}</div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
        <button onClick={() => navigate("/blog")} className="inline-flex items-center gap-2 text-sm text-navy hover:text-gold mb-8"><ArrowLeft size={18} className="rtl:rotate-180" /> {t("blog.all")}</button>

        <article className="prose-yad">
          <ReactMarkdown
            components={{
              h2: ({ node, ...props }) => <h2 className="font-display text-2xl text-navy mt-10 mb-3" {...props} />,
              h3: ({ node, ...props }) => <h3 className="font-display text-xl text-navy mt-6 mb-2" {...props} />,
              p: ({ node, ...props }) => <p className="text-muted-foreground leading-relaxed mb-4" {...props} />,
              ul: ({ node, ...props }) => <ul className="list-disc ps-6 space-y-2 mb-4 text-muted-foreground" {...props} />,
              ol: ({ node, ...props }) => <ol className="list-decimal ps-6 space-y-2 mb-4 text-muted-foreground" {...props} />,
              strong: ({ node, ...props }) => <strong className="text-navy font-semibold" {...props} />,
              a: ({ node, ...props }) => <a className="text-gold underline" {...props} />,
            }}
          >
            {post.body}
          </ReactMarkdown>
        </article>

        <div className="mt-12 bg-navy text-white rounded-2xl p-8 text-center">
          <h3 className="font-display text-2xl mb-3">{t("blog.cta_title")}</h3>
          <p className="text-white/60 mb-5">{t("blog.cta_text")}</p>
          <Link to="/biens" className="inline-block bg-gold text-navy font-semibold px-7 py-3.5 rounded-full hover:bg-[#C8924A] transition-colors">{t("blog.cta_btn")}</Link>
        </div>
      </div>
    </div>
  );
};

export default BlogArticle;
